/**
 * Flattened PROJECT MARKUP PDF export.
 *
 * Captures a dedicated export-only surface (HEX/RGB inline styles) rather than
 * the live interactive workspace. html2canvas still clones document stylesheets,
 * so onclone strips all app CSS that may contain oklch/oklab/color-mix.
 */
import { createRoot, type Root } from "react-dom/client";
import { createElement } from "react";
import ProjectMarkupExportCanvas, {
  PROJECT_MARKUP_EXPORT_ROOT_ATTR,
} from "@/components/sandbox/project-markup/ProjectMarkupExportCanvas";
import type {
  MarkupCanvasSize,
  MarkupPlanInformation,
  MarkupPlanTransform,
  PlacedMarker,
  ProjectMarkupComponent,
} from "@/lib/projectMarkupTypes";
import {
  DEFAULT_PROJECT_MARKUP_CANVAS,
  DEFAULT_PROJECT_MARKUP_PLAN_TRANSFORM,
} from "@shared/projectMarkupLayoutDocument";

export const PROJECT_MARKUP_EXPORT_SAFE_CLASS = "project-markup-export-safe";

export const PROJECT_MARKUP_EXPORT_FAILED_MESSAGE =
  "Export failed because the plan contains unsupported styling. Please try again after refreshing.";

const UNSUPPORTED_COLOR_RE = /okl(ch|ab)\([^)]*\)|color-mix\([^)]*\)/gi;

const COLOR_STYLE_PROPS = [
  "color",
  "background",
  "background-color",
  "background-image",
  "border-color",
  "border-top-color",
  "border-right-color",
  "border-bottom-color",
  "border-left-color",
  "outline-color",
  "fill",
  "stroke",
  "stop-color",
  "flood-color",
  "lighting-color",
  "column-rule-color",
  "text-decoration-color",
  "caret-color",
  "box-shadow",
  "text-shadow",
  "filter",
  "webkit-filter",
  "webkit-text-fill-color",
  "webkit-text-stroke-color",
] as const;

export function replaceUnsupportedColorFunctionsForTest(css: string): string {
  return css.replace(UNSUPPORTED_COLOR_RE, "#111111");
}

export function isUnsupportedColorExportError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? "");
  return (
    /okl(ch|ab)\(/i.test(message) ||
    /unsupported color function/i.test(message) ||
    /color-mix\(/i.test(message)
  );
}

function replaceUnsupportedInCssText(css: string, fallback = "#111111"): string {
  return css.replace(UNSUPPORTED_COLOR_RE, fallback);
}

function sanitizeElementColors(el: HTMLElement | SVGElement) {
  const style = el.style;
  for (const prop of COLOR_STYLE_PROPS) {
    const value = style.getPropertyValue(prop);
    if (value && UNSUPPORTED_COLOR_RE.test(value)) {
      UNSUPPORTED_COLOR_RE.lastIndex = 0;
      if (prop === "box-shadow" || prop === "text-shadow" || prop === "filter" || prop === "webkit-filter") {
        style.setProperty(prop, "none", "important");
      } else if (prop === "background" || prop === "background-image") {
        style.setProperty(prop, "#ffffff", "important");
      } else if (prop === "fill" || prop === "stroke") {
        // Prefer marker HEX already set; fall back to black
        style.setProperty(prop, replaceUnsupportedInCssText(value, "#111111"), "important");
      } else {
        style.setProperty(prop, replaceUnsupportedInCssText(value), "important");
      }
    }
    UNSUPPORTED_COLOR_RE.lastIndex = 0;
  }

  // Also scrub cssText in case shorthand landed unsupported values
  if (style.cssText && UNSUPPORTED_COLOR_RE.test(style.cssText)) {
    UNSUPPORTED_COLOR_RE.lastIndex = 0;
    style.cssText = replaceUnsupportedInCssText(style.cssText);
  }
  UNSUPPORTED_COLOR_RE.lastIndex = 0;
}

/**
 * Strip every stylesheet/link from the html2canvas clone and inject only
 * export-safe rules. This is the critical step — html2canvas parses global
 * Tailwind/theme CSS which resolves to oklch even when the live node is sanitized.
 */
export function prepareProjectMarkupExportClone(clonedDoc: Document, clonedRoot: HTMLElement) {
  clonedDoc.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
    node.remove();
  });

  const style = clonedDoc.createElement("style");
  style.setAttribute("data-project-markup-export", "true");
  style.textContent = `
    html, body {
      background: #ffffff !important;
      color: #111111 !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    [${PROJECT_MARKUP_EXPORT_ROOT_ATTR}] {
      background: #ffffff !important;
      color: #111111 !important;
    }
    [${PROJECT_MARKUP_EXPORT_ROOT_ATTR}] * {
      box-shadow: none !important;
      filter: none !important;
      text-shadow: none !important;
    }
  `;
  clonedDoc.head.appendChild(style);

  clonedRoot.classList.add(PROJECT_MARKUP_EXPORT_SAFE_CLASS);
  clonedRoot.style.backgroundColor = "#ffffff";
  clonedRoot.style.color = "#111111";

  // Hide any leftover interactive chrome if present in clone
  clonedRoot.querySelectorAll<HTMLElement>("[data-markup-export-hide]").forEach((el) => {
    el.style.display = "none";
  });

  // Scrub unsupported colour functions that may have been copied as inline styles
  // from getComputedStyle on the live theme-aware document.
  const walk = clonedRoot.querySelectorAll<HTMLElement | SVGElement>("*");
  sanitizeElementColors(clonedRoot);
  walk.forEach((el) => sanitizeElementColors(el));
}

function waitForImages(root: HTMLElement, timeoutMs = 8000): Promise<void> {
  const images = Array.from(root.querySelectorAll("img"));
  if (images.length === 0) return Promise.resolve();

  return new Promise((resolve) => {
    let remaining = images.length;
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };
    const done = () => {
      remaining -= 1;
      if (remaining <= 0) finish();
    };
    const timer = window.setTimeout(finish, timeoutMs);
    for (const img of images) {
      if (img.complete) {
        done();
        continue;
      }
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    }
  });
}

export type MarkupExportSnapshot = {
  planImageDataUrl: string;
  /** Natural plan bitmap size used for aspect / fit math. */
  planNaturalWidth: number;
  planNaturalHeight: number;
  markers: PlacedMarker[];
  components: ProjectMarkupComponent[];
  canvas?: MarkupCanvasSize;
  planTransform?: MarkupPlanTransform;
  planInformation?: MarkupPlanInformation | null;
};

async function mountExportSurface(snapshot: MarkupExportSnapshot): Promise<{
  host: HTMLDivElement;
  root: Root;
  canvasRoot: HTMLElement;
}> {
  const host = document.createElement("div");
  host.setAttribute("data-project-markup-export-host", "true");
  host.style.cssText =
    "position:fixed;left:-10000px;top:0;z-index:-1;background:#ffffff;pointer-events:none;";
  document.body.appendChild(host);

  const componentsById = new Map(snapshot.components.map((c) => [c.id, c]));
  const root = createRoot(host);
  root.render(
    createElement(ProjectMarkupExportCanvas, {
      planImageDataUrl: snapshot.planImageDataUrl,
      planNaturalWidth: snapshot.planNaturalWidth,
      planNaturalHeight: snapshot.planNaturalHeight,
      markers: snapshot.markers,
      componentsById,
      canvas: snapshot.canvas ?? DEFAULT_PROJECT_MARKUP_CANVAS,
      planTransform: snapshot.planTransform ?? DEFAULT_PROJECT_MARKUP_PLAN_TRANSFORM,
      planInformation: snapshot.planInformation ?? null,
    }),
  );

  // Allow React commit + image decode
  await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
  const canvasRoot = host.querySelector<HTMLElement>(`[${PROJECT_MARKUP_EXPORT_ROOT_ATTR}]`);
  if (!canvasRoot) {
    root.unmount();
    host.remove();
    throw new Error("Export render failed.");
  }
  await waitForImages(canvasRoot);
  return { host, root, canvasRoot };
}

function cleanupExportSurface(host: HTMLDivElement, root: Root) {
  try {
    root.unmount();
  } catch {
    // ignore
  }
  host.remove();
}

async function html2canvasExportNode(node: HTMLElement) {
  const { default: html2canvas } = await import("html2canvas");
  if (import.meta.env.DEV) {
    console.info("[ProjectMarkupExport] html2canvas start", {
      width: node.offsetWidth,
      height: node.offsetHeight,
    });
  }
  try {
    return await html2canvas(node, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#ffffff",
      logging: import.meta.env.DEV,
      onclone: (clonedDoc, clonedElement) => {
        prepareProjectMarkupExportClone(clonedDoc, clonedElement);
      },
    });
  } catch (err) {
    console.error("[ProjectMarkupExport] html2canvas failed", err);
    throw err;
  }
}

async function canvasToPdfBase64(canvas: HTMLCanvasElement): Promise<string> {
  const { jsPDF } = await import("jspdf");
  try {
    const imgData = canvas.toDataURL("image/png");
    const orientation = canvas.width >= canvas.height ? "landscape" : "portrait";
    const pdf = new jsPDF({ orientation, unit: "px", format: [canvas.width, canvas.height] });
    pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
    const dataUri = pdf.output("datauristring");
    const base64 = dataUri.split(",")[1] ?? "";
    if (!base64) throw new Error("Failed to generate PDF.");
    return base64;
  } catch (err) {
    console.error("[ProjectMarkupExport] jsPDF failed", err);
    throw err;
  }
}

/**
 * Shared export path for DOWNLOAD AS PDF and SHARE WITH CUSTOMER.
 */
export async function captureProjectMarkupPdfBase64(snapshot: MarkupExportSnapshot): Promise<string> {
  if (!snapshot.planImageDataUrl) {
    throw new Error("Export render failed.");
  }
  if (!Number.isFinite(snapshot.planNaturalWidth) || snapshot.planNaturalWidth <= 0) {
    throw new Error("Export render failed.");
  }

  let mounted: Awaited<ReturnType<typeof mountExportSurface>> | null = null;
  try {
    if (import.meta.env.DEV) {
      console.info("[ProjectMarkupExport] mount export surface", {
        markers: snapshot.markers.length,
        planNaturalWidth: snapshot.planNaturalWidth,
        planNaturalHeight: snapshot.planNaturalHeight,
      });
    }
    mounted = await mountExportSurface(snapshot);
    const canvas = await html2canvasExportNode(mounted.canvasRoot);
    return await canvasToPdfBase64(canvas);
  } catch (err) {
    if (isUnsupportedColorExportError(err)) {
      console.error("[ProjectMarkupExport] unsupported colour still present after sanitize", err);
    }
    throw err;
  } finally {
    if (mounted) cleanupExportSurface(mounted.host, mounted.root);
  }
}

/** @deprecated Prefer captureProjectMarkupPdfBase64 with an export snapshot. */
export async function captureMarkupNodeAsPdfBase64(node: HTMLElement): Promise<string> {
  const canvas = await html2canvasExportNode(node);
  return canvasToPdfBase64(canvas);
}

export async function downloadProjectMarkupPdf(snapshot: MarkupExportSnapshot, filename = "project-markup-export.pdf") {
  const base64 = await captureProjectMarkupPdfBase64(snapshot);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
